import { backend_api_v2 } from '/src/utils/apiv2'
import base from "./base"
import { defineStore } from 'pinia'
import bus from '/src/utils/event_bus'
import { project_label as build_project_label } from '/src/utils/company'
import { useSessionStore } from '/src/stores/session'
// import router from '/src/router'

// In-flight request for the placeholder catalogue, outside the state so it is
// never serialized and never reactive.
let _placeholders_request = null


export const useCompanyStore = defineStore('company', {
    state: () => ({
        company: { meta: {} },
        standard_libraries: [],
        projects: [],
        archived_projects: [],
        users: [],
        api_accounts: [],
        licenses: [],
        ifc_ps: [],
        multipanel_configs: [],
        contacts: [],
        // What can go between the braces of a `{placeholder}` template, per
        // context, plus the company's own model keys. Loaded once per session
        // by whichever form asks first: the catalogue is a constant and the
        // key list is a sample of the company's panels, so neither moves while
        // a settings dialog is open.
        placeholders: null,
    }),
    getters: {
        projects_and_stdlibs: state => [...state.projects, ...state.standard_libraries],
        projects_and_stdlibs_with_archived: state => [...state.projects, ...state.archived_projects, ...state.standard_libraries],
        show_project_number: state => !!state.company?.meta?.show_project_number,
        // BTLx files as the model of a design (woodwall docs/wrappers/btlx-master-file-plan.md).
        // A release switch on the company, off by default; the backend enforces it too.
        btlx_master_files: state => !!state.company?.meta?.btlx_master_files,
        // Every view that shows a project goes through this getter, so the
        // company setting stays reactive without a watcher per consumer.
        project_label() {
            return (project) => {
                if (!this.show_project_number)
                    return { number: "", name: project?.name || "", show_number: false }
                return build_project_label(project)
            }
        },
    },
    actions: {
        // My Company
        setCompany(data) { this.company = data },

        // Standard Libraries
        setStandardLibraries: base.actions.setter("standard_libraries"),
        createStandardLibrary: base.actions.creator("standard_libraries"),
        updateStandardLibrary: base.actions.updater("standard_libraries"),
        removeStandardLibrary: base.actions.remover("standard_libraries"),


        // Projects
        setProjects: base.actions.setter("projects"),
        setArchivedProjects: base.actions.setter("archived_projects"),
        createProject: base.actions.creator("projects"),
        updateProject: base.actions.updater("projects"),
        removeProject: base.actions.remover("projects"),

        // Users
        setUsers: base.actions.setter("users"),
        createUser: base.actions.creator("users"),
        updateUser: base.actions.updater("users"),
        removeUser: base.actions.remover("users"),

        // API Accounts
        setAPIAccounts: base.actions.setter("api_accounts"),
        createAPIAccount: base.actions.creator("api_accounts"),
        updateAPIAccount: base.actions.updater("api_accounts"),
        removeAPIAccount: base.actions.remover("api_accounts"),

        // Licenses
        setLicenses: base.actions.setter("licenses"),
        createLicense: base.actions.creator("licenses"),
        updateLicense: base.actions.updater("licenses"),
        removeLicense: base.actions.remover("licenses"),

        // IFCPS
        setIFCPSs: base.actions.setter("ifc_ps"),
        createIFCPS: base.actions.creator("ifc_ps"),
        updateIFCPS: base.actions.updater("ifc_ps"),
        removeIFCPS: base.actions.remover("ifc_ps"),


        // Multipanel Configs
        setMultipanelConfigs: base.actions.setter("multipanel_configs"),
        createMultipanelConfig: base.actions.creator("multipanel_configs"),
        updateMultipanelConfig: base.actions.updater("multipanel_configs"),
        removeMultipanelConfig: base.actions.remover("multipanel_configs"),

        // Contacts
        setContacts: base.actions.setter("contacts"),
        createContact: base.actions.creator("contacts"),
        updateContact: base.actions.updater("contacts"),
        removeContact: base.actions.remover("contacts"),


        // My Company
        loadCompany() {
            return backend_api_v2.get('/my_company/')
                .then(({ data }) => {
                    this.setCompany(data)
                })
                .catch(e => console.log(`Cant load Compnay: ${e}`))
        },
        patchCompany(payload) {
            return backend_api_v2.put('/my_company/', payload)
                .then(({ data }) => {
                    this.setCompany(data)
                    // The session carries its own copy of the company, and every
                    // company-setting getter reads THAT one. Without this the
                    // row was written and the app went on using the old value
                    // until the next login.
                    useSessionStore().syncCompany(data)
                })
                .catch(e => console.log(`Cant patch company: ${e}`))
        },
        // Patch single keys of company.meta. Prefer this over patchCompany({meta})
        // for anything stored under meta: sending the whole blob means two admins
        // saving different settings discard each other's key.
        // A key set to null is removed from meta.
        // Rejects on failure, so a Save button can keep its dialog open.
        patchCompanyMeta(partial_meta) {
            return backend_api_v2.patch('/my_company/meta/', partial_meta)
                .then(({ data }) => {
                    this.setCompany(data)
                    // Same reason as patchCompany: several getters read
                    // `company.meta` out of the session's nested copy.
                    useSessionStore().syncCompany(data)
                    return data
                })
                .catch(e => {
                    bus.emit('notification', {
                        timeout: 5000, color: "error",
                        text: `Can't save settings: ${JSON.stringify(e.response?.data)}`,
                    })
                    throw e
                })
        },

        // The `{placeholder}` vocabulary. `_placeholders_request` is shared so
        // three fields mounting at once cost one request, not three.
        loadPlaceholders() {
            if (this.placeholders) return Promise.resolve(this.placeholders)
            if (_placeholders_request) return _placeholders_request
            _placeholders_request = backend_api_v2.get('/my_company/placeholders/')
                .then(({ data }) => {
                    this.placeholders = data
                    return data
                })
                .catch(e => {
                    console.log(`Cant load placeholders: ${e}`)
                    return null
                })
                .finally(() => { _placeholders_request = null })
            return _placeholders_request
        },

        // Standard Libraries
        loadStandardLibraries: base.actions.loader("company/standard_libraries/", "StandardLibraries"),
        postStandardLibrary: base.actions.poster("company/standard_libraries/", "StandardLibrary"),
        putStandardLibrary: base.actions.putter("company/standard_libraries/", "StandardLibrary"),
        deleteStandardLibrary: base.actions.deleter("company/standard_libraries/", "StandardLibrary"),

        // Projects
        loadProjects: base.actions.loader("company/projects/", "Projects"),
        loadArchivedProjects: base.actions.loader("company/projects/?archived=true", "ArchivedProjects"),
        postProject: base.actions.poster("company/projects/", "Project"),
        putProject: base.actions.putter("company/projects/", "Project"),
        deleteProject: base.actions.deleter("company/projects/", "Project"),
        updateProjectResources(payload) {
            let project_uuid = payload.project.uuid
            return backend_api_v2.put('company/projects/' + project_uuid + '/patch_resources/', payload.data)
                .then(({ data }) => {
                    this.refreshProject(data)
                })
                .catch(e => console.log(`Cant update project resources: ${e}`))
        },

        // Users
        loadUsers: base.actions.loader("company/users/", "Users"),
        postUser: base.actions.poster("company/users/", "User"),
        putUser: base.actions.putter("company/users/", "User"),
        deleteUser: base.actions.deleter("company/users/", "User"),
        postUserPassword(payload) {
            return backend_api_v2.put('company/users/' + payload.id + "/change_password/", payload)
                .then(({ data }) =>
                    bus.emit("notification", {
                        timeout: 3000,
                        text: data.detail,
                        color: "success",
                    })
                )
                .catch(e => bus.emit("notification", {
                    timeout: 3000,
                    text: e,
                    color: "error",
                }))
        },

        // API Accounts
        loadAPIAccounts: base.actions.loader("company/api_accounts/", "APIAccounts"),
        postAPIAccount: base.actions.poster("company/api_accounts/", "APIAccount"),
        putAPIAccount: base.actions.putter("company/api_accounts/", "APIAccount"),
        deleteAPIAccount: base.actions.deleter("company/api_accounts/", "APIAccount"),

        // Licenses
        loadLicenses: base.actions.loader("company/licenses/", "Licenses"),
        postLicense: base.actions.poster("company/licenses/", "License"),
        putLicense: base.actions.putter("company/licenses/", "License"),
        deleteLicense: base.actions.deleter("company/licenses/", "License"),

        // IFCPS
        loadIFCPSs: base.actions.loader("company/ifc_ps/", "IFCPSs"),
        postIFCPS: base.actions.poster("company/ifc_ps/", "IFCPS"),
        // Saving never analyses (docs/ifcps-save-analysis-proposal.md). A
        // version that projects use answers 409 for a content change: the
        // editor publishes a new version instead. Name, default and locks
        // belong to the whole setup, so the list is reloaded after them.
        putIFCPS(payload) {
            return backend_api_v2.put(`company/ifc_ps/${payload.uuid}/`, payload)
                .then(({ data }) => {
                    this.updateIFCPS(data)
                    const shared = ["name", "is_default", "read_only", "company_lock"]
                    if (shared.some((k) => k in payload)) this.loadIFCPSs()
                    return { data }
                })
                .catch(e => {
                    bus.emit('notification', { timeout: 5000, text: `Can't update IFCPS: ${JSON.stringify(e.response?.data)}`, color: "error" })
                })
        },
        // The next version of a setup with the content sent. No project moves.
        publishIFCPS(uuid, payload) {
            return backend_api_v2.post(`company/ifc_ps/${uuid}/publish/`, payload)
                .then(async ({ data }) => {
                    await this.loadIFCPSs()
                    return { data }
                })
                .catch(e => {
                    bus.emit('notification', { timeout: 5000, text: `Can't publish IFCPS: ${JSON.stringify(e.response?.data)}`, color: "error" })
                })
        },
        // What moving projects to this version costs. `projects` null lists
        // the live projects of the setup that are on another version.
        moveProjectsPreview(uuid, projects = null) {
            const body = { dry_run: true }
            if (projects) body.projects = projects
            return backend_api_v2.post(`company/ifc_ps/${uuid}/move_projects/`, body)
                .then(({ data }) => data)
        },
        moveProjects(uuid, projects) {
            return backend_api_v2.post(`company/ifc_ps/${uuid}/move_projects/`, { projects })
                .then(async ({ data }) => {
                    await Promise.all([this.loadIFCPSs(), this.loadProjects()])
                    return data
                })
        },
        // Who changed a setup, what and when, every version, newest first.
        ifcpsHistory(uuid) {
            return backend_api_v2.get(`company/ifc_ps/${uuid}/history/`).then(({ data }) => data)
        },
        deleteIFCPS: base.actions.deleter("company/ifc_ps/", "IFCPS"),


        // Multipanel Configs
        loadMultipanelConfigs: base.actions.loader("constructibles/multipanel_configs/", "MultipanelConfigs"),
        postMultipanelConfig: base.actions.poster("constructibles/multipanel_configs/", "MultipanelConfig"),
        putMultipanelConfig: base.actions.putter("constructibles/multipanel_configs/", "MultipanelConfig"),
        deleteMultipanelConfig: base.actions.deleter("constructibles/multipanel_configs/", "MultipanelConfig"),

        // Contacts
        loadContacts: base.actions.loader("company/contacts/", "Contacts"),
        postContact: base.actions.poster("company/contacts/", "Contact"),
        putContact: base.actions.putter("company/contacts/", "Contact"),
        deleteContact: base.actions.deleter("company/contacts/", "Contact"),
    },
})
